import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator';
import { Field, Input } from '@hb/coffee';
import { trimAll, trimValue } from '@/shared/utils/trim';
import UserPersonalInfoApi from '@/api/userPersonalInfo';
import { validatorPersonName } from '@/shared/utils/validator';
import { UserVo } from '@/vo/user';
import store from '@/store';
import { nameMaskFormat } from '@/shared/utils/dataMaskFormat';
import { ContactsGroupContext, SupplementInfoField } from '@/components/supplement-info-fields/types';
import { isEmptyFieldValue } from '@/components/supplement-info-fields/utils';
import { shouldShowContactImportIcon } from '@/views_v2/verify-credit/show-contact-icon';
import { handleImportClick } from '@/shared/utils/contactImport/handleImport';
import { ubtSetupModuleExpose } from '@/shared/utils/ubt/ubt-setup';
import { TP_CONTACT_IMPORT_SUCCESS } from '@/views_v2/verify-credit/tianqi';

const style = require('./index.module.scss') as Dictionary;

@Component({ name: 'ContactName' })
export default class ContactName extends Vue implements SupplementInfoField {
  @Prop()
  private value!: string;

  @Prop(Array) readonly contextOtherContacts!: ContactsGroupContext;

  @Prop(Boolean) readonly showMask!: boolean | undefined;

  @Prop(Number) readonly contactIndex!: number;

  private contactName = '';

  private errorMsg = '';

  private get showImportIcon(): boolean {
    return !!(store.state.verifyCredit && store.state.verifyCredit.shouldShowContactImportIcon);
  }

  /** 检验联系人姓名-正确性 */
  public async onValidator(): Promise<boolean> {
    const val = this.contactName;

    /** 脱敏时，不校验正确性 */
    if (this.showMask) {
      return true;
    }

    if (isEmptyFieldValue(val)) {
      this.errorMsg = '请填写联系人的姓名';
      return false;
    }

    if (!validatorPersonName(val)) {
      this.errorMsg = '联系人姓名只能包含2～25个汉字';
      return false;
    }

    /** 联系人姓名不能包含敏感字符 */
    const sensitiveCharacter = this.checkSensitiveName(val);
    if (sensitiveCharacter) {
      this.errorMsg = `联系人姓名不能包含【${sensitiveCharacter}】等称谓词`;
      return false;
    }

    if (!await this.checkSameToUserName(val)) {
      this.errorMsg = '请勿输入本人姓名';
      return false;
    }

    if (!this.checkGroupNameRepeate(val)) {
      this.errorMsg = '联系人姓名不能够重复';
      return false;
    }

    return true;
  }

  private async created() {
    if (this.value) {
      this.contactName = this.value;
    }
    await shouldShowContactImportIcon();
  }

  @Watch('value')
  private onPropsValueChange(val: string) {
    /** 删除空格字符 */
    this.contactName = trimAll(val);
  }

  /** 联系人姓名不能包含敏感字符 */
  private checkSensitiveName(val: string): string | undefined {
    const trimVal = trimValue(val);
    // 当前需要校验的联系人名字敏感字符
    const SentiveNameList = [
      '姥', '爷', '婆', '公', '奶',
      '爸', '妈', '叔', '姨', '舅',
      '哥', '兄', '姐', '弟', '妹',
      '丈夫', '老公', '老婆', '妻子', '媳妇',
      '父', '母', '伯', '婶', '姑',
      '婿', '嫂',
    ];
    const checkNameRes = SentiveNameList.find(
      (item) => trimVal.indexOf(item) > -1,
    );

    return checkNameRes;
  }

  /** 联系人姓名不能和注册姓名一致 */
  private async checkSameToUserName(val: string): Promise<boolean> {
    const trimVal = trimValue(val);
    const { checkNameRes } = await UserPersonalInfoApi
      .checkUserContact({ contactName: trimVal }) || {};

    return !checkNameRes;
  }

  /** 若存在多个联系人时，姓名不能重复 */
  private checkGroupNameRepeate(val: string): boolean {
    const name = trimValue(val);

    if (
      Array.isArray(this.contextOtherContacts)
      && this.contextOtherContacts.some(
        (item: UserVo.ContactInfo) => item.contactName === name,
      )) {
      return false;
    }

    this.errorMsg = '';
    return true;
  }

  private onChangeName(val: string) {
    if (this.showMask) {
      /**
       * 脱敏状态下的处理：
       * 1. 真实value无变化，拦截无效的change事件
       * 2. 真实value发生变化，先清空value
       */
      if (val !== nameMaskFormat(this.value)) {
        /** 脱敏状态，首次输入更新时，清空value */
        this.$emit('change', '');
      }

      return;
    }

    this.errorMsg = '';
    this.$emit('change', val);
  }

  private onBlur() {
    this.onValidator();
    this.$emit('blur');
  }

  private formatter(value: string) {
    if (this.showMask) {
      return nameMaskFormat(value);
    }

    return value;
  }

  /** 仅在哈啰app内且不包括鸿蒙系统，以及版本为6.98.70及以上，才允许导入联系人 */
  private async onImportClick() {
    const importedContactInfo = await handleImportClick();
    if (importedContactInfo) {
      // 导入成功后，更新姓名、手机号
      this.$emit('importName', importedContactInfo.name, true);
      this.$emit('importPhone', importedContactInfo.phone, true);
      ubtSetupModuleExpose(TP_CONTACT_IMPORT_SUCCESS);
      // 导入后触发校验，确保字段校验逻辑执行
      this.$nextTick(() => {
        this.onValidator();
      });
    }
  }

  private render() {
    return (
      <Field
        label="姓名"
        errorMessage={this.errorMsg}
        scopedSlots={{
          icon: () => (this.showImportIcon && (
            <div
              class={style.contactNameIcon}
              onClick={() => this.onImportClick()}
            />
          )),
        }}
      >
        <Input
          v-model={this.contactName}
          placeholder="填写联系人的姓名"
          formatter={this.formatter.bind(this)}
          on={{
            'update:modelValue': (val: string) => {
              this.onChangeName(val);
            },
          }}
          onBlur={this.onBlur.bind(this)}
        />
      </Field>
    );
  }
}
